/**
 * Customer Profile Completion with Backend API
 * Handles profile completion for logged-in customers missing required information
 * Uses localStorage to track dismissal with configurable delay
 */

(function () {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const modal = document.getElementById('profile-completion-modal');
    if (!modal) {
      console.error('Profile completion modal not found');
      return;
    }

    const form = document.getElementById('customer-profile-completion-form');
    const dismissButton = document.getElementById('profile-completion-dismiss');
    const phoneInput = document.getElementById('profile-completion-phone');

    // Setup phone input formatting
    if (phoneInput) {
      phoneInput.addEventListener('input', formatPhoneNumber);
    }

    // Check if modal should be shown
    if (shouldShowModal()) {
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        modal.show();
      }, 500);
    }

    // Setup form submission
    if (form) {
      form.addEventListener('submit', handleFormSubmit);
    } else {
      console.error('Profile completion form not found');
    }

    // Setup dismiss link
    if (dismissButton) {
      dismissButton.addEventListener('click', (e) => {
        e.preventDefault();
        handleDismiss();
      });
    }

    // Setup tooltip click handlers
    setupTooltipHandlers();

    // Also handle modal close button (in shadow DOM) as a dismissal
    modal.addEventListener('close', handleDismiss);
  }

  function setupTooltipHandlers() {
    // Handle tooltip click for mobile/accessibility
    const tooltipIcons = document.querySelectorAll('.tooltip-icon');

    tooltipIcons.forEach((icon) => {
      icon.addEventListener('click', (e) => {
        e.preventDefault();
        icon.classList.toggle('tooltip-active');

        // Close other active tooltips
        tooltipIcons.forEach((otherIcon) => {
          if (otherIcon !== icon) {
            otherIcon.classList.remove('tooltip-active');
          }
        });
      });

      // Close tooltip when clicking outside
      document.addEventListener('click', (e) => {
        if (!icon.contains(e.target)) {
          icon.classList.remove('tooltip-active');
        }
      });

      // Handle keyboard navigation
      icon.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          icon.click();
        }
      });
    });
  }

  function shouldShowModal() {
    // Check if modal was dismissed recently
    const dismissedUntil = localStorage.getItem('profile-completion-dismissed-until');

    if (dismissedUntil) {
      const dismissedDate = new Date(dismissedUntil);
      const now = new Date();

      if (now < dismissedDate) {
        return false;
      }
    }

    return true;
  }

  function handleDismiss() {
    // Get dismiss days from settings (default 3 days)
    const dismissDays = window.Shopify?.profileCompletionDismissDays || 3;

    // Calculate future date
    const dismissUntil = new Date();
    dismissUntil.setDate(dismissUntil.getDate() + dismissDays);

    // Save to localStorage
    localStorage.setItem('profile-completion-dismissed-until', dismissUntil.toISOString());

    // Close modal
    const modal = document.getElementById('profile-completion-modal');
    if (modal) {
      modal.hide();
    }
  }

  async function handleFormSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const submitButton = document.getElementById('profile-completion-submit');
    const formData = new FormData(form);

    // Clear previous errors
    clearErrors();
    hideMessages();

    // Validate form
    const validation = validateForm(formData);
    if (!validation.isValid) {
      showErrors(validation.errors);
      return;
    }

    // Show loading state
    setLoading(submitButton, true);

    try {
      // Update customer via backend API
      const result = await updateCustomer(formData);

      if (result.success) {
        showSuccess(result.message || 'Profile updated successfully!');

        // Clear the dismissal timestamp since profile is now complete
        localStorage.removeItem('profile-completion-dismissed-until');

        // Close modal after brief delay
        const modal = document.getElementById('profile-completion-modal');
        if (modal) {
          setTimeout(() => {
            modal.hide();
            // Reload page to reflect updated customer data
            window.location.reload();
          }, 1500);
        }
      } else {
        showErrors(result.errors);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      showGlobalError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(submitButton, false);
    }
  }

  async function updateCustomer(formData) {
    const customerId = formData.get('customerId')?.trim();
    const firstName = formData.get('firstName')?.trim() || undefined;
    const lastName = formData.get('lastName')?.trim() || undefined;
    const phone = formData.get('phone')?.trim() || undefined;
    const dateOfBirth = formData.get('dateOfBirth')?.trim() || undefined;

    // Get backend configuration
    const backendUrl = window.Shopify?.backendUrl;
    const storeIdentifier = window.Shopify?.storeIdentifier || 'pilot-coffee';

    if (!backendUrl) {
      console.error('Backend URL not configured');
      return {
        success: false,
        errors: { _global: 'Configuration error. Please contact support.' },
      };
    }

    if (!customerId) {
      console.error('Customer ID not provided');
      return {
        success: false,
        errors: { _global: 'Customer ID is required.' },
      };
    }

    const payload = {
      customerId,
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      ...(phone && { phone }),
      ...(dateOfBirth && { dateOfBirth }),
    };

    try {
      const response = await fetch(`${backendUrl}/api/customers/update?store=${storeIdentifier}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      // Backend returns { success: boolean, errors?: object, customer?: object, message?: string }
      if (data.success) {
        return {
          success: true,
          customer: data.customer,
          message: data.message,
        };
      } else {
        return {
          success: false,
          errors: data.errors || { _global: 'Failed to update profile. Please try again.' },
        };
      }
    } catch (error) {
      console.error('API error:', error);
      return {
        success: false,
        errors: { _global: 'Network error. Please check your connection and try again.' },
      };
    }
  }

  function validateForm(formData) {
    const errors = {};

    const firstName = formData.get('firstName');
    const lastName = formData.get('lastName');
    const phone = formData.get('phone');
    const dateOfBirth = formData.get('dateOfBirth');

    // Only validate fields that are present in the form (not null)
    // FormData.get() returns null if field doesn't exist, empty string if empty

    // First name is REQUIRED
    if (firstName !== null) {
      const trimmedFirstName = firstName.trim();
      if (!trimmedFirstName) {
        errors.firstName = 'First name is required';
      }
    }

    // Last name is OPTIONAL - no validation needed
    // (User can leave it blank)

    // Phone is REQUIRED
    if (phone !== null) {
      const trimmedPhone = phone.trim();
      if (!trimmedPhone) {
        errors.phone = 'Phone number is required';
      } else if (!isValidPhone(trimmedPhone)) {
        errors.phone = 'Please enter a valid phone number with country code (e.g., +1 555-123-4567)';
      }
    }

    // Date of birth is OPTIONAL - only validate format if provided
    if (dateOfBirth !== null) {
      const trimmedDob = dateOfBirth.trim();
      if (trimmedDob && !isValidDate(trimmedDob)) {
        errors.dateOfBirth = 'Please enter a valid date';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  function isValidPhone(phone) {
    // Basic validation: must start with + and contain at least 10 digits
    const phoneRegex = /^\+[\d\s\-()]{10,}$/;
    return phoneRegex.test(phone);
  }

  function isValidDate(dateString) {
    // Check if date is in valid format and is a real date
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  }

  function showErrors(errors) {
    Object.keys(errors).forEach((field) => {
      if (field === '_global') {
        showGlobalError(errors[field]);
      } else {
        const errorDiv = document.getElementById(`error-profile-${field}`);
        if (errorDiv) {
          errorDiv.textContent = errors[field];
          errorDiv.style.display = 'block';

          // Add error class to the form control
          const formControl = errorDiv.previousElementSibling;
          if (formControl && formControl.classList.contains('form-control')) {
            formControl.classList.add('has-error');
          }
        }
      }
    });
  }

  function clearErrors() {
    document.querySelectorAll('.form-error').forEach((error) => {
      error.textContent = '';
      error.style.display = 'none';

      // Remove error class from the form control
      const formControl = error.previousElementSibling;
      if (formControl && formControl.classList.contains('form-control')) {
        formControl.classList.remove('has-error');
      }
    });
  }

  function showGlobalError(message) {
    const errorBanner = document.getElementById('profile-completion-error');
    const errorMessage = document.getElementById('profile-completion-error-message');
    if (errorBanner && errorMessage) {
      errorMessage.textContent = message;
      errorBanner.style.display = 'block';
    }
  }

  function showSuccess(message) {
    const successBanner = document.getElementById('profile-completion-success');
    const successMessage = document.getElementById('profile-completion-success-message');
    if (successBanner && successMessage) {
      successMessage.textContent = message;
      successBanner.style.display = 'block';
    }
  }

  function hideMessages() {
    const errorBanner = document.getElementById('profile-completion-error');
    const successBanner = document.getElementById('profile-completion-success');
    if (errorBanner) errorBanner.style.display = 'none';
    if (successBanner) successBanner.style.display = 'none';
  }

  function setLoading(button, isLoading) {
    if (!button) return;

    const label = button.querySelector('.button-label');
    const spinner = button.querySelector('.button-spinner');

    if (isLoading) {
      button.classList.add('is-loading');
      button.disabled = true;
      if (spinner) spinner.style.display = 'inline-block';
    } else {
      button.classList.remove('is-loading');
      button.disabled = false;
      if (spinner) spinner.style.display = 'none';
    }
  }

  function formatPhoneNumber(event) {
    const input = event.target;
    const hiddenInput = document.getElementById('profile-completion-phone-full');

    // Get only digits from the input
    let value = input.value.replace(/\D/g, '');

    // Limit to 10 digits
    value = value.substring(0, 10);

    // Format as (XXX) XXX-XXXX
    let formatted = '';
    if (value.length > 0) {
      formatted = '(' + value.substring(0, 3);
    }
    if (value.length >= 4) {
      formatted += ') ' + value.substring(3, 6);
    }
    if (value.length >= 7) {
      formatted += '-' + value.substring(6, 10);
    }

    // Update visible input
    input.value = formatted;

    // Update hidden input with full format +1XXXXXXXXXX
    if (hiddenInput && value.length === 10) {
      hiddenInput.value = '+1' + value;
    } else if (hiddenInput) {
      hiddenInput.value = '';
    }
  }
})();
